import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary, btnSecondary } from '../styles/theme.js'
import { projects as projectsApi, uploads as uploadsApi, auditLog as auditLogApi, bookmarksApi, notificationsApi, myActivityApi } from '../lib/api.js'

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const titleRow = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${space.xl};
`

const projectTitle = css`
  font-size: ${font.size['3xl']};
  font-weight: ${font.weight.bold};
  line-height: 1.2;
`

const desc = css`
  font-size: ${font.size.base};
  color: ${colors.textSecondary};
  margin-top: ${space.sm};
  max-width: 600px;
  line-height: 1.6;
`

const statsRow = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${space.md};
  margin-bottom: ${space.xl};
`

const statCard = css`
  ${card};
  text-align: center;
  padding: ${space.md};
`

const statValue = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  color: ${colors.brand};
`

const statLabel = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: ${space.xs};
`

const sectionTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const navGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: ${space.md};
  margin-bottom: ${space.xl};
`

const navCard = css`
  ${card};
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: ${colors.brand}; }
`

const navCardTitle = css`
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.xs};
`

const navCardDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
`

const uploadRow = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const uploadName = css`
  font-weight: ${font.weight.medium};
`

const uploadMeta = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
`

const activityRow = css`
  display: flex;
  gap: ${space.sm};
  padding: ${space.xs} ${space.md};
  border-bottom: 1px solid ${colors.border};
  font-size: ${font.size.sm};
  &:last-child { border-bottom: none; }
`

const activityIcon = css`
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 7px;
`

const activityText = css`
  flex: 1;
  color: ${colors.textSecondary};
  line-height: 1.5;
`

const activityLink = css`
  flex: 1;
  color: ${colors.textSecondary};
  line-height: 1.5;
  text-decoration: none;
  &:hover { color: ${colors.brand}; }
`

const activityTime = css`
  flex-shrink: 0;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  white-space: nowrap;
`

// Format audit log entry for human reading
export function formatActivity(entry, memberMap) {
  const { entity_type, action, user_id, changes: changesRaw } = entry
  const who = memberMap?.get(user_id) || user_id?.slice(0, 8) || 'Someone'

  let changes = null
  if (changesRaw) {
    try {
      changes = typeof changesRaw === 'string' ? JSON.parse(changesRaw) : changesRaw
    } catch { changes = null }
  }

  const msg = detailedMessage(entity_type, action, changes)
  return `${who} ${msg}`
}

// Extract session ID from an audit log entry, if one is available
export function getSessionId(entry) {
  const { entity_type, entity_id, action, changes: changesRaw } = entry

  // session delete: entity_id is the session ID, but the session no longer exists
  if (entity_type === 'session' && action === 'delete') return null

  // Direct session reference
  if (entity_type === 'session') return entity_id

  // Parse changes JSON for sessionId field
  let changes = null
  if (changesRaw) {
    try {
      changes = typeof changesRaw === 'string' ? JSON.parse(changesRaw) : changesRaw
    } catch { return null }
  }

  if (changes?.sessionId) return changes.sessionId

  return null
}

function detailedMessage(entityType, action, changes) {
  const name = changes?.name
  // Tag updates store name as [oldVal, newVal] array
  const tagName = Array.isArray(name) ? name[1] : name
  const filename = changes?.filename
  const title = changes?.title
  const sessionTagName = changes?.tagName
  const sessionCount = changes?.sessionCount

  switch (entityType) {
    case 'upload':
      if (action === 'create') return filename ? `uploaded '${filename}'` : 'uploaded a file'
      if (action === 'ingest') return sessionCount != null ? `ingested ${sessionCount} sessions` : 'ingested sessions'
      if (action === 'delete') return sessionCount != null ? `deleted upload (${sessionCount} sessions)` : 'deleted an upload'
      break
    case 'tag':
      if (action === 'create') return tagName ? `created tag '${tagName}'` : 'created a tag'
      if (action === 'update') return tagName ? `updated tag '${tagName}'` : 'updated a tag'
      if (action === 'delete') return tagName ? `deleted tag '${tagName}'` : 'deleted a tag'
      break
    case 'session_tag':
      if (action === 'create') return sessionTagName ? `tagged a session with '${sessionTagName}'` : 'tagged a session'
      if (action === 'delete') return sessionTagName ? `untagged '${sessionTagName}' from a session` : 'untagged a session'
      break
    case 'rubric':
      if (action === 'create') return name ? `created rubric '${name}'` : 'created a rubric'
      if (action === 'update') return 'updated a rubric'
      break
    case 'evaluation':
      if (action === 'create') return 'submitted an evaluation'
      if (action === 'update') return 'updated an evaluation'
      break
    case 'highlight':
      if (action === 'create') return 'added a highlight'
      if (action === 'delete') return 'removed a highlight'
      break
    case 'highlight_type':
      if (action === 'create') return name ? `created highlight type '${name}'` : 'created a highlight type'
      break
    case 'comment':
      if (action === 'create') return 'posted a comment'
      if (action === 'delete') return 'deleted a comment'
      break
    case 'task':
      if (action === 'create') return title ? `created task '${title}'` : 'created a task'
      if (action === 'close') return title ? `closed task '${title}'` : 'closed a task'
      break
    case 'task_invite':
      if (action === 'create') return 'sent a task invite'
      if (action === 'accept') return 'accepted a task invite'
      break
  }

  return `${action} ${entityType}`
}

function actionColor(entityType) {
  const map = {
    upload: colors.teal,
    tag: colors.purple,
    session_tag: colors.purple,
    rubric: colors.orange,
    evaluation: colors.blue,
    highlight: colors.yellow,
    comment: colors.green,
    task: colors.pink,
    task_invite: colors.pink,
  }
  return map[entityType] || colors.textMuted
}

function timeAgo(isoString) {
  if (!isoString) return ''
  // SQLite datetime('now') stores UTC as 'YYYY-MM-DD HH:MM:SS' without T/Z.
  // Without 'Z', JS Date() treats it as local time, breaking the diff.
  const normalized = isoString.includes('T') ? isoString : isoString.replace(' ', 'T')
  const d = new Date(normalized.endsWith('Z') ? normalized : normalized + 'Z')
  const now = new Date()
  const diffMs = now - d
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

export function ProjectView() {
  let project = null
  let uploadList = null
  let activityList = null
  let loading = true
  let isOwner = false

  // My Work data
  let myBookmarks = null
  let myActivityData = null
  let unreadCount = 0

  async function load(vnode) {
    loading = true
    const projectId = vnode.attrs.projectId
    try {
      const [proj, ul, activity] = await Promise.all([
        projectsApi.get(projectId),
        uploadsApi.list(projectId),
        auditLogApi.list(projectId, { limit: 20 }),
      ])
      project = proj
      isOwner = proj.memberRole === 'owner'
      uploadList = ul
      activityList = activity

      // Load user-scoped data (non-blocking)
      Promise.all([
        bookmarksApi.list(projectId, { limit: 3 }),
        myActivityApi.list(projectId, { limit: 1 }),
        notificationsApi.unreadCount(projectId),
      ]).then(([bm, act, nc]) => {
        myBookmarks = bm
        myActivityData = act
        unreadCount = nc?.count || 0
        m.redraw()
      }).catch(() => {})
    } catch (err) {
      console.error('Failed to load project:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  return {
    oninit: load,
    view(vnode) {
      const projectId = vnode.attrs.projectId

      if (loading) return m('div', 'Loading...')
      if (!project) return m('div', 'Project not found')

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          project.name,
        ]),

        m('div', { class: titleRow }, [
          m('div', [
            m('h1', { class: projectTitle }, project.name),
            project.description
              ? m('p', { class: desc }, project.description)
              : null,
          ]),
        ]),

        // My Work section
        (() => {
          const hasBookmarks = myBookmarks && myBookmarks.bookmarks && myBookmarks.bookmarks.length > 0
          const hasActivity = myActivityData?.sessions?.length > 0
          const hasAnyData = hasBookmarks || hasActivity || unreadCount > 0
          if (!hasAnyData) return null
          return m('div', [
            m('h2', { class: sectionTitle }, 'My work'),
            m('div', { class: statsRow }, [
              (() => {
                const items = []
                items.push(m('div', { style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: ${space.sm};` }, 'Bookmarks'))
                if (hasBookmarks) {
                  for (const b of myBookmarks.bookmarks) {
                    items.push(m('div', { style: `margin-bottom: 4px;` },
                      m(m.route.Link, {
                        href: `/projects/${projectId}/sessions/${b.session_id}`,
                        style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; text-decoration: none;`,
                      }, b.external_id || b.session_id.slice(0, 8))
                    ))
                  }
                } else {
                  items.push(m('div', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; font-style: italic;` }, 'No bookmarks yet'))
                }
                if (myBookmarks && myBookmarks.total > 3) {
                  items.push(m(m.route.Link, {
                    href: `/projects/${projectId}/browser?bm=1`,
                    style: `font-size: ${font.size.xs}; color: ${colors.brand};`,
                  }, `View all ${myBookmarks.total}`))
                }
                return m('div', { class: `${statCard}`, style: 'text-align: left;' }, items)
              })(),
              m('div', { class: statCard }, [
                m('div', { class: statLabel }, 'Sessions you\'ve worked on'),
                m(m.route.Link, {
                  href: `/projects/${projectId}/browser?mine=1`,
                  style: `font-size: ${font.size.xs}; color: ${colors.brand}; display: inline-block; margin-top: ${space.sm};`,
                }, 'Browse'),
              ]),
              m('div', { class: statCard }, [
                m('div', { class: statValue, style: unreadCount > 0 ? `color: ${colors.brand};` : '' }, unreadCount),
                m('div', { class: statLabel }, 'Unread notifications'),
                m(m.route.Link, {
                  href: `/projects/${projectId}/notifications`,
                  style: `font-size: ${font.size.xs}; color: ${colors.brand}; display: inline-block; margin-top: ${space.sm};`,
                }, 'View all'),
              ]),
            ]),
          ])
        })(),

        m('h2', { class: sectionTitle }, 'Quick actions'),
        (() => {
          const cards = []
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/browser`),
          }, [
            m('div', { class: navCardTitle }, 'Browse sessions'),
            m('div', { class: navCardDesc }, 'View, search, and filter chat sessions'),
          ]))
          if (isOwner) {
            cards.push(m('div', {
              class: navCard,
              onclick: () => m.route.set(`/projects/${projectId}/upload`),
            }, [
              m('div', { class: navCardTitle }, 'Upload data'),
              m('div', { class: navCardDesc }, 'Import CSV or JSON chat data'),
            ]))
          }
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/tags`),
          }, [
            m('div', { class: navCardTitle }, 'Tags'),
            m('div', { class: navCardDesc }, 'Create and manage tags for categorizing sessions'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/rubrics`),
          }, [
            m('div', { class: navCardTitle }, 'Rubrics'),
            m('div', { class: navCardDesc }, 'Create and manage evaluation rubrics'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/eval-summary`),
          }, [
            m('div', { class: navCardTitle }, 'Eval Summary'),
            m('div', { class: navCardDesc }, 'Aggregate grade distribution across rubrics and sessions'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/tasks`),
          }, [
            m('div', { class: navCardTitle }, 'Tasks'),
            m('div', { class: navCardDesc }, 'Create tasks and invite guest reviewers'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/clusters`),
          }, [
            m('div', { class: navCardTitle }, 'Clusters & Similarity'),
            m('div', { class: navCardDesc }, 'Find patterns — embed sessions, cluster, and discover similar conversations'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/predictions`),
          }, [
            m('div', { class: navCardTitle }, 'Tag Predictions'),
            m('div', { class: navCardDesc }, 'Use AI to automatically suggest and apply tags across sessions'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/pii-detection`),
          }, [
            m('div', { class: navCardTitle }, 'PII Detection'),
            m('div', { class: navCardDesc }, 'Scan sessions for emails, phone numbers, SSNs, and other PII'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/notifications`),
          }, [
            m('div', { class: navCardTitle }, [
              'Notifications',
              unreadCount > 0
                ? m('span', {
                    style: `display: inline-flex; align-items: center; justify-content: center; background: ${colors.brand}; color: white; font-size: 10px; font-weight: ${font.weight.semibold}; border-radius: 50%; width: 18px; height: 18px; margin-left: 6px;`,
                  }, unreadCount)
                : null,
            ]),
            m('div', { class: navCardDesc }, 'Replies to your comments and session activity'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/export`),
          }, [
            m('div', { class: navCardTitle }, 'Export data'),
            m('div', { class: navCardDesc }, 'Download sessions, tags, evaluations, and audit log as JSON'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/scenarios`),
          }, [
            m('div', { class: navCardTitle }, 'Scenarios'),
            m('div', { class: navCardDesc }, 'Generate synthetic test scenarios with rubric variants'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/styles`),
          }, [
            m('div', { class: navCardTitle }, 'Style Presets'),
            m('div', { class: navCardDesc }, 'Manage communication styles for scenario generation'),
          ]))
          cards.push(m('div', {
            class: navCard,
            onclick: () => m.route.set(`/projects/${projectId}/settings`),
          }, [
            m('div', { class: navCardTitle }, 'Settings'),
            m('div', { class: navCardDesc }, 'Project members, tags, and configuration'),
          ]))
          return m('div', { class: navGrid }, cards)
        })(),

        // Upload history
        uploadList && uploadList.length > 0
          ? [
              m('h2', { class: sectionTitle }, 'Upload history'),
              m('div', { class: card },
                uploadList.map((u) =>
                  m('div', { class: uploadRow }, [
                    m('div', [
                      m('div', { class: uploadName }, u.filename),
                      m('div', { class: uploadMeta },
                        `${u.format.toUpperCase()} — ${u.row_count ?? '?'} sessions — ${new Date(u.uploaded_at).toLocaleDateString()}`),
                    ]),
                  ])
                )
              ),
            ]
          : null,

        // Activity feed
        activityList && activityList.length > 0
          ? (() => {
              const memberMap = new Map()
              if (project.members) {
                for (const mem of project.members) {
                  memberMap.set(mem.id, mem.name || mem.email)
                }
              }
              return [
                m('h2', { class: sectionTitle }, 'Recent activity'),
                m('div', { class: card },
                  activityList.map((entry) => {
                    const sessionId = getSessionId(entry)
                    const text = formatActivity(entry, memberMap)
                    return m('div', { key: entry.id, class: activityRow }, [
                      m('span', {
                        class: activityIcon,
                        style: `background: ${actionColor(entry.entity_type)};`,
                      }),
                      sessionId
                        ? m(m.route.Link, {
                            href: `/projects/${projectId}/sessions/${sessionId}`,
                            class: activityLink,
                          }, text)
                        : m('span', { class: activityText }, text),
                      m('span', { class: activityTime }, timeAgo(entry.created_at)),
                    ])
                  })
                ),
              ]
            })()
          : null,
      ])
    },
  }
}
